import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

// Importing from analytics but this should only be temp while design updates to the new tab format.
import {TabButtonElement} from '../../../../../../../../src/analytics/components/tabs/tabButtonElement';
import {ContactsManagerSearchTabsEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {ContactsManagerHeader} from '../../contactsManagerHeader';
import {ContactsManagerTabs} from '../../contactsManagerTabs';
import {ContactsTableMultiSelectActions} from '../../tables/common/contacts/contactsTableMultiSelectActions';
import {AccountsSearchTableContainer} from '../../tables/search/accountsSearchTableContainer';
import {CardGroupsSearchTableContainer} from '../../tables/search/cardGroupsSearchTableContainer';
import {ContactsSearchTableContainer} from '../../tables/search/contactsSearchTableContainer';

/*
 * Props.
 */

interface SearchPageProps {
  panelId: ContactsManagerPanelId;
  selectedTab: ContactsManagerSearchTabsEnum;
  selectedCardIds: ReadonlyArray<number>;
  onTabSelect: HandlerOf<ContactsManagerSearchTabsEnum>;
}

/*
 * Styles.
 */

const StyledEmptyStateContainerDiv = styled.div`
  text-align: center;
  padding: 32px 0;
  position: absolute;
  top: 36px;
  left: 0;
  right: 0;
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const SearchPage: FC<SearchPageProps> = (props) => {
  const {panelId, selectedTab, selectedCardIds, onTabSelect} = props;
  return (
    <>
      <ContactsManagerHeader title={<Trans id="contacts-manager-search-title">Search results</Trans>} />
      {renderTabs(selectedTab, onTabSelect)}
      {renderSearchTabTable(panelId, selectedTab, selectedCardIds)}
    </>
  );
};

/*
 * Helpers.
 */

function renderSearchTabTable(
  panelId: ContactsManagerPanelId,
  selectedTab: ContactsManagerSearchTabsEnum,
  selectedCardIds: ReadonlyArray<number>,
) {
  switch (selectedTab) {
    case ContactsManagerSearchTabsEnum.ACCOUNTS:
      return (
        <AccountsSearchTableContainer
          panelId={panelId}
          renderEmptyState={() => renderEmptyState(selectedTab)}
        />
      );
    case ContactsManagerSearchTabsEnum.CARD_GROUPS:
      return (
        <CardGroupsSearchTableContainer
          panelId={panelId}
          renderEmptyState={() => renderEmptyState(selectedTab)}
        />
      );
    case ContactsManagerSearchTabsEnum.CONTACTS:
    default:
      return (
        <ContactsSearchTableContainer
          panelId={panelId}
          multiSelectHeaderActions={
            <ContactsTableMultiSelectActions isAddToListsButtonVisible cardIds={selectedCardIds} />
          }
          renderEmptyState={() => renderEmptyState(selectedTab)}
        />
      );
  }
}

function renderTabs(
  selectedTab: ContactsManagerSearchTabsEnum,
  onTabSelect: HandlerOf<ContactsManagerSearchTabsEnum>,
) {
  return (
    <ContactsManagerTabs>
      <TabButtonElement
        name={<Trans id="contacts-manager-search-contacts-tab-title">Contacts</Trans>}
        isSelected={selectedTab === ContactsManagerSearchTabsEnum.CONTACTS}
        onTabClick={() => onTabSelect(ContactsManagerSearchTabsEnum.CONTACTS)}
      />
      <TabButtonElement
        name={<Trans id="contacts-manager-search-card-groups-tab-title">Lists</Trans>}
        isSelected={selectedTab === ContactsManagerSearchTabsEnum.CARD_GROUPS}
        onTabClick={() => onTabSelect(ContactsManagerSearchTabsEnum.CARD_GROUPS)}
      />
      <TabButtonElement
        name={<Trans id="contacts-manager-search-accounts-tab-title">Accounts</Trans>}
        isSelected={selectedTab === ContactsManagerSearchTabsEnum.ACCOUNTS}
        onTabClick={() => onTabSelect(ContactsManagerSearchTabsEnum.ACCOUNTS)}
      />
    </ContactsManagerTabs>
  );
}

function renderEmptyState(selectedTab: ContactsManagerSearchTabsEnum) {
  return <StyledEmptyStateContainerDiv>{renderEmptyStateMessage(selectedTab)}</StyledEmptyStateContainerDiv>;
}

function renderEmptyStateMessage(selectedTab: ContactsManagerSearchTabsEnum) {
  if (selectedTab === ContactsManagerSearchTabsEnum.ACCOUNTS) {
    return <Trans id="contacts-manager-search-accounts-empty-state">No accounts matched your search.</Trans>;
  }
  if (selectedTab === ContactsManagerSearchTabsEnum.CARD_GROUPS) {
    return <Trans id="contacts-manager-search-card-groups-empty-state">No lists matched your search.</Trans>;
  }
  return <Trans id="contacts-manager-search-contacts-empty-state">No contacts matched your search.</Trans>;
}
